import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
} from "react";
import { nanoid } from "nanoid";
import { resolveLocalized } from "@shared/i18n";
import { offerIsSellable, slotPolicyOf } from "@shared/roomOffers";
import { guestMayCancel } from "@shared/guestRequests";
import type { LocalizedString } from "@shared/types/foyer";
import type {
  GuestRequest,
  GuestRequestStatus,
} from "@shared/types/guestRequest";
import type {
  Money,
  OfferItem,
  OfferSlotAvailability,
  TvRoomOffer,
} from "@shared/types/roomOffer";
import { startDwell, trackEngagement } from "../../../lib/engagement";
import { useGuestRequest } from "../../../hooks/useGuestRequest";
import { RemoteHint } from "../RemoteHint";
import { useScheme } from "../schemeContext";
import { useFocusGrid } from "../useFocusGrid";
import OfferPicker, {
  applyPick,
  applyStep,
  pickerColumns,
  selectedLines,
  selectionTotal,
  MAX_QUANTITY,
  type OfferSelection,
  type PickerItem,
  type PickerMode,
  type PickerRow,
} from "./OfferPicker";
import {
  formatMoney,
  formatSlot,
  formatSlotDay,
  offersCopy,
  type OffersCopy,
} from "./offersCopy";

/**
 * The Offers screen: what the hotel is selling, on the television (`POS-3`).
 *
 * **One renderer, eight hotel templates**, exactly as
 * `../custom/CustomScreen.tsx` already established — a template supplies an
 * {@link OffersScreenStyle} built from its own tokens and nothing else. Eight
 * offers pages would be eight keyboard models to debug on a remote and eight
 * places a fourth action kind has to be added, which is how this feature
 * becomes unmaintainable (`docs/room-offers.md` §2).
 *
 * Two levels and no more: a RAIL of cards, and a DETAIL page behind each one.
 *
 * ## The guest flow lives here too (`POS-7`)
 *
 * `detail → choose → confirm → pending → confirmed`, driven by up / down /
 * left / right / OK / Back and nothing else. It is inside {@link OfferDetail}
 * rather than in a ninth component, so the eight hotel templates keep sharing
 * ONE keyboard model — and there is exactly one picker
 * ({@link OfferPicker}) for all three kinds, because all three are the same
 * interaction: pick from a list, then confirm (`docs/room-offers.md` §2).
 *
 * There is no "pay how" step. Charge-to-room is the only method in the demo
 * (§6), so the confirm step STATES where the money goes instead of asking; a
 * choice with one option in it is a keypress that teaches a guest nothing, and
 * the card path (POS-13) is deliberately out of this set.
 *
 * The status flip from pending to confirmed comes over POS-6's realtime hub and
 * is never simulated locally — see `useGuestRequest`.
 *
 * ## What the feed already decided
 *
 * `GET /premise/:id/offers` has ALREADY dropped disabled rows, out-of-window
 * rows and disabled `items` (POS-2). Nothing here re-filters: a second, slightly
 * different copy of the availability rules on the client is exactly how a guest
 * ends up looking at an offer the server will refuse. If something unwanted
 * reaches this component that is a server bug to report, not a client
 * workaround.
 *
 * `remaining` on a slot is real API surface and is rendered truthfully. It
 * equals `capacity` until POS-6 registers the seat counter, at which point it
 * starts falling with no change here.
 */

export interface OffersScreenStyle {
  /** Page ground behind the rail. */
  bg: string;
  /** Card surface. */
  card: string;
  /** Primary text. */
  ink: string;
  /** Secondary text — body copy, meta lines. */
  inkDim: string;
  /** Hairline around cards and between sections. */
  rule: string;
  /** Focus ring, price, and the kind badge's fill. */
  accent: string;
  /** Text colour ON the accent fill. */
  onAccent: string;
  /** Display font for titles. */
  titleFont: string;
  /** Body font. */
  font: string;
  /** Corner radius the template uses on its own cards. */
  radius: number;
  /** Tone behind a card with no photograph. */
  placeholder: string;
}

interface Props {
  style: OffersScreenStyle;
  /** Leaves the screen — the Back key on the rail. */
  onBack: () => void;
  scopeRef: RefObject<HTMLElement>;
  /**
   * What the premise calls this screen (`offersTitle`). Blank falls back to the
   * copy pack's own word, so the heading is never empty.
   */
  title?: string;
  /**
   * Inner padding. A host that owns the whole viewport passes the TV-safe
   * constants (the default); a host that has already inset its sub-pages — the
   * launcher family's `LauncherPage` — passes zeroes, or the page is indented
   * twice and loses a third of its width.
   */
  pad?: { x: number; y: number };
  /**
   * Draw the remote-hint strip along the foot.
   *
   * On by default, because the launcher family's sub-pages carry no persistent
   * footer and a two-level screen has to say that Back exists. The bespoke three
   * pass `false`: their own `Footer` already renders the same strip, and two of
   * them stacked reads as a rendering fault.
   */
  hints?: boolean;
  /**
   * Open straight into one offer's detail page — the deep link **POS-4** needs.
   *
   * The home banner IS the entry point for the booking flow, so pressing it has
   * to land on that offer's page rather than on a rail the guest then has to
   * find it in again. Read once, on mount, and deliberately not watched
   * afterwards: a prop that re-opened a page whenever it changed would re-open
   * it over whatever the guest was doing when the home slot rotated.
   *
   * An id the feed does not carry is ignored, which lands on the rail — the
   * same answer a guest gets for an offer that went out of its window between
   * the banner rendering and the press.
   */
  initialOfferId?: string;
}

/**
 * The TV-safe area, same constants as `CustomScreen`. Nothing meaningful may sit
 * in the outer ~5% of a panel: the bezel eats it, and which 5% depends on the
 * television rather than on us.
 */
export const SAFE_X = 96;
export const SAFE_Y = 72;

/** Cards in the rail. Wide enough for a title and two lines of body at 3 m. */
const CARD_W = 460;
const CARD_GAP = 28;
/**
 * How much of a long list a detail page shows before it says "+ n more".
 *
 * There is a ceiling because the detail page does not scroll: it has one focus
 * cell (Back), so anything past the fold would be unreachable rather than merely
 * below. A truncation that SAYS it truncated is honest; content silently cut off
 * by `overflow: hidden` is not. POS-7's picker is what shows the whole list, and
 * it can scroll because it has focus to scroll with.
 */
/**
 * How many cards of the rail are on screen at once.
 *
 * 1920 minus the safe area, over a 460 card plus its gap — three whole cards
 * and a sliver of a fourth. It is what `offer_view` counts, so it is a fact
 * about the layout below rather than a taste decision: get it wrong and the
 * impression figure measures the catalogue instead of the guest.
 */
const VISIBLE_CARDS = 3;
const SLOT_PREVIEW = 6;
const ITEM_PREVIEW = 8;
/**
 * How wide a row of the PICKER gets before it wraps.
 *
 * Different numbers because they are different questions: hours are small and
 * read as a timetable, so six sit comfortably across a 1080p panel inside the
 * safe area; amenity tiles carry an icon and a name, so four is the most that
 * stays legible at three metres. Neither list is ever truncated — the picker
 * scrolls, because unlike the detail page it has focus to scroll with.
 */
const SLOTS_PER_ROW = 6;
const ITEMS_PER_ROW = 4;

/**
 * The cheapest price anywhere on an offer, and whether it is the offer's own.
 *
 * An offer may price itself, price each line, or neither. A card that showed
 * nothing for a room-service menu whose lines all carry prices would read as
 * free, so the card falls back to "from <cheapest line>" — and says `from`, so
 * it is never mistaken for the total.
 *
 * Only lines in the FIRST currency are compared. Mixing currencies on one offer
 * is pathological, but comparing across them by `amount` alone would headline a
 * "9 €" line as cheaper than a "95 kr" one — a wrong price is worse than a
 * missing one.
 */
function cardPrice(
  offer: TvRoomOffer
): { price: { amount: number; currency: string }; from: boolean } | null {
  const own =
    offer.action.kind === "promotion" ? undefined : offer.action.price;
  // "from" when the offer prices ITSELF and its items are priced too: §2 rules
  // that an item price "overrides nothing, adds to the line", so 450 kr with a
  // 120 kr item on it is a floor, not the price. Headlining the bare 450 would
  // understate what the guest is about to be charged - and the confirm step,
  // which sums them, would then contradict the card they pressed.
  if (own) return { price: own, from: offer.items.some((i) => i.price) };
  const priced = offer.items
    .map((it) => it.price)
    .filter((p): p is NonNullable<typeof p> => !!p);
  if (priced.length === 0) return null;
  const currency = priced[0].currency;
  const cheapest = priced
    .filter((p) => p.currency === currency)
    .reduce((a, b) => (b.amount < a.amount ? b : a));
  return { price: cheapest, from: true };
}

export default function OffersScreen({
  style,
  onBack,
  scopeRef,
  title,
  pad = { x: SAFE_X, y: SAFE_Y },
  hints = true,
  initialOfferId,
}: Props) {
  const scheme = useScheme();
  const copy = offersCopy(scheme.context.locale);
  const feed = scheme.offers;
  // Memoised so the empty-feed case is one stable array rather than a new one
  // per render: two effects below watch it, and a fresh `[]` every render would
  // re-run them forever.
  const feedOffers = useMemo(() => feed?.offers ?? [], [feed]);
  // The RAIL only ever lists SELLABLE offers - order/booking, the things this
  // screen actually transacts. A `promotion`-kind row has no sell mechanics at
  // all (`requestable` below is false for it) and exists purely to be
  // featured on the home screen (`homePromo` slot, gated by `showPromotions`)
  // - see `offerIsSellable`. Deliberately a SEPARATE array from `feedOffers`
  // rather than the same one filtered in place: `live`/`open` below still
  // resolve against the full feed, because a guest who pressed a promotion
  // straight off the home screen (`initialOfferId`, POS-4) must still land on
  // its detail page - it is only the browsable MENU that excludes it.
  const offers = useMemo(
    () => feedOffers.filter((o) => offerIsSellable(o.action)),
    [feedOffers]
  );
  const L = useMemo(() => {
    const locale = scheme.context.locale ?? "en";
    const fallback = scheme.context.defaultLocale ?? locale;
    return (v: LocalizedString | undefined) =>
      resolveLocalized(v, locale, fallback);
  }, [scheme.context.locale, scheme.context.defaultLocale]);

  // Which card is open, held by ID rather than by index: the feed refreshes on
  // its own timer, and an index would silently re-point at a different offer the
  // moment one sold out underneath the guest.
  const [openId, setOpenId] = useState<string | null>(initialOfferId ?? null);
  // True once the guest has started a flow (chosen, confirmed, or is waiting on
  // reception) — see the hold below.
  const [inFlow, setInFlow] = useState(false);
  // Against the FULL feed, not the sellable-only rail: a promotion-kind offer
  // is a legitimate deep-link target (`initialOfferId`, pressed from the home
  // slot) even though it never appears as a card here.
  const live = feedOffers.find((o) => o.id === openId) ?? null;
  // The offer as it was when the flow started.
  //
  // The feed re-polls every five minutes and an offer can go out of its window
  // while a guest is mid-order — and, worse, while they are watching a pending
  // request turn into a confirmed one. Dropping the page then would take away
  // the confirmation for a request the hotel has already accepted. So a started
  // flow HOLDS its page; only an untouched detail is dismissed by the feed.
  const heldRef = useRef<TvRoomOffer | null>(null);
  useEffect(() => {
    if (live) heldRef.current = live;
  }, [live]);
  const open = live ?? (inFlow ? heldRef.current : null);
  // An offer that leaves the feed drops the guest back to the rail - and the id
  // has to go with it. An offer on a daily window (room service 17:00-24:00)
  // comes BACK on a later poll, and a surviving id would re-open its detail page
  // by itself, over whatever the guest was doing.
  useEffect(() => {
    // Not before the feed has ANSWERED. `feedOffers` is `[]` while the request
    // is still on the wire (and again whenever the premise changes), and
    // effects run after the first commit whatever the render returned — so
    // without this guard POS-4's `initialOfferId` was cleared on mount and the
    // deep link landed on the rail every time. Checked against the FULL feed,
    // same reasoning as `live` above - a promotion-kind deep link must not be
    // dropped as "left the feed" just because it was never in the rail.
    if (!feed) return;
    if (openId && !inFlow && !feedOffers.some((o) => o.id === openId)) {
      setOpenId(null);
    }
  }, [feed, openId, inFlow, feedOffers]);

  const [focus, setFocus] = useFocusGrid({
    rows: 1,
    cols: Math.max(1, offers.length),
    scopeRef,
    wrap: true,
    enabled: !open,
    onEnter: (f) => {
      const picked = offers[f.c];
      if (picked) setOpenId(picked.id);
    },
    onBack,
  });
  // The focus grid holds its own index across a feed that shrank under it, so a
  // guest sitting on the last card when an offer expires would be left with no
  // ring on any card and an OK that does nothing until they pressed an arrow.
  // Clamped for READING; the grid's own state self-heals on the next press.
  const focusIdx = Math.min(focus.c, Math.max(0, offers.length - 1));

  // Impressions — the funnel's first stage (POS-11).
  //
  // The cards the RAIL IS ACTUALLY SHOWING, which is the window the shift below
  // slides: a catalogue of ten booking ten impressions the moment the screen
  // opens would measure the operator's authoring rather than a guest's
  // attention.
  //
  // Each one is TIMED, mount-to-unmount, exactly like a promotion slot's
  // `slot_view` (`SchemeSlot`'s `SlotImpression`) — because they are the same
  // measurement and POS-11's funnel is built on the slot report's semantics
  // rather than on a parallel counter that would drift from it. A card the
  // guest arrowed past in 300ms and one they sat in front of for half a minute
  // are not the same impression, and only the duration can tell them apart
  // afterwards; `startDwell` drops anything under a second outright, and the
  // server counts anything over `SLOT_ENGAGED_SECONDS` as engaged.
  //
  // What rides along: the offer id and its title, and nothing else. **No room,
  // no price, no request id, no seat and no hour** — those five are what
  // `docs/room-offers.md` §5 is protecting, and an offer id is none of them: it
  // names a row of the hotel's own catalogue, identically for every guest and
  // every room in the building, exactly as the app, station and slot ids already
  // in `engagement_daily` do.
  const visible = useMemo(() => {
    const from = Math.max(0, focusIdx - 1);
    return offers.slice(from, from + VISIBLE_CARDS);
  }, [offers, focusIdx]);

  const frame: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    width: "100%",
    boxSizing: "border-box",
    padding: `${pad.y}px ${pad.x}px`,
    background: style.bg,
    color: style.ink,
    fontFamily: style.font,
    overflow: "hidden",
  };

  if (!feed) {
    return (
      <div style={frame} data-testid="offers-screen">
        <Heading
          style={style}
          eyebrow={copy.eyebrow}
          title={title || copy.title}
        />
        <Note style={style}>{copy.loading}</Note>
      </div>
    );
  }

  if (open) {
    return (
      <div style={frame} data-testid="offers-screen">
        <OfferDetail
          // Remounted per offer, so a guest who backs out and opens another one
          // never inherits the previous page's selection or its request.
          key={open.id}
          offer={open}
          style={style}
          copy={copy}
          L={L}
          scopeRef={scopeRef}
          hints={hints}
          onFlow={setInFlow}
          onClose={() => {
            setInFlow(false);
            setOpenId(null);
          }}
        />
      </div>
    );
  }

  // Keep the focused card on screen without a scroll container: the rail slides
  // under a clipped window. `scrollIntoView` would fight the d-pad on a panel
  // whose smooth-scroll lands a frame late, and a native scrollbar has no place
  // on a television.
  const shift = Math.max(0, focusIdx - 1) * (CARD_W + CARD_GAP);

  return (
    <div style={frame} data-testid="offers-screen">
      <Heading
        style={style}
        eyebrow={copy.eyebrow}
        title={title || copy.title}
      />
      {/* Draws nothing; its lifetime IS the impression's. Rendered only on the
          rail branch, so opening a detail page ends every card's visit. */}
      {visible.map((offer) => (
        <OfferImpression
          key={offer.id}
          offerId={offer.id}
          label={L(offer.title)}
        />
      ))}
      {offers.length === 0 ? (
        <Note style={style}>{copy.empty}</Note>
      ) : (
        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              gap: CARD_GAP,
              height: "100%",
              transform: `translateX(${-shift}px)`,
              transition: "transform 260ms cubic-bezier(0.2,0.8,0.2,1)",
            }}
          >
            {offers.map((offer, i) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                style={style}
                copy={copy}
                L={L}
                focused={focusIdx === i}
                onMouseEnter={() => setFocus({ r: 0, c: i })}
                onClick={() => setOpenId(offer.id)}
              />
            ))}
          </div>
        </div>
      )}
      {hints && (
        <Hints
          style={style}
          items={[
            { key: "◄ ►", label: copy.hint.move },
            { key: "OK", label: copy.hint.open },
            { key: "←", label: copy.hint.back },
          ]}
        />
      )}
    </div>
  );
}

/**
 * Times one offer card's visit and reports it when it ends (`POS-11`).
 *
 * Its own component, and one that renders nothing, for the reason
 * `SlotImpression` gives: mounted only on the visible path, the effect's
 * lifetime IS the impression's lifetime, with no visibility bookkeeping of its
 * own and no hook sitting behind a conditional return. React unmounts it when
 * the card leaves the rail's window, when the guest opens a detail page, and
 * when they leave the screen — three exits, one cleanup.
 *
 * ⚠️ **The visit's identity is `offerId` alone, and `label` is deliberately not
 * a dependency.** A guest switching interface language while the rail is on
 * screen, or a feed poll picking up an operator's title edit, changes the
 * rendered string — and if that restarted the dwell it would split ONE
 * continuous visit into two impressions, either of which may fall under
 * `SLOT_ENGAGED_SECONDS` when the real visit cleared it. That understates
 * `engagedRate`, which the doctrine calls the closest honest thing to a
 * conversion rate a television can produce. So the name rides in a ref: it is
 * cosmetic (the server keeps a last-seen display name and never aggregates on
 * it), and a visit reported under the title it OPENED with is right anyway.
 */
function OfferImpression({
  offerId,
  label,
}: {
  offerId: string;
  label: string;
}) {
  const labelRef = useRef(label);
  useEffect(() => {
    labelRef.current = label;
  }, [label]);
  useEffect(
    () => startDwell("offer_view", offerId, labelRef.current || undefined),
    [offerId]
  );
  return null;
}

// ── chrome ──────────────────────────────────────────────────────────────────

function Heading({
  style,
  eyebrow,
  title,
}: {
  style: OffersScreenStyle;
  eyebrow: string;
  title: string;
}) {
  return (
    <div style={{ flex: "0 0 auto", marginBottom: 26 }}>
      <div
        style={{
          fontSize: 17,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: style.accent,
          fontWeight: 600,
          marginBottom: 10,
        }}
      >
        {eyebrow}
      </div>
      <h1
        style={{
          margin: 0,
          fontFamily: style.titleFont,
          fontWeight: 600,
          fontSize: 56,
          // 1.2, not tighter: this box clips, and a tight line-height slices the
          // descenders off g/y/p/j/q on a TV. See
          // docs/memory/global/pitfalls/tv-tight-line-height-clipping.md.
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h1>
    </div>
  );
}

function Note({
  style,
  children,
}: {
  style: OffersScreenStyle;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid="offers-note"
      style={{
        flex: "1 1 auto",
        display: "flex",
        alignItems: "center",
        fontSize: 30,
        lineHeight: 1.3,
        color: style.inkDim,
      }}
    >
      {children}
    </div>
  );
}

function Hints({
  style,
  items,
}: {
  style: OffersScreenStyle;
  items: { key: string; label: string }[];
}) {
  return (
    <div style={{ flex: "0 0 auto", marginTop: 22, color: style.inkDim }}>
      <RemoteHint
        items={items.map((it) => ({
          ...it,
          bg: style.card,
          fg: style.ink,
          border: `1px solid ${style.rule}`,
        }))}
      />
    </div>
  );
}

/** The small caps chip that says what pressing this offer will eventually do. */
function Badge({
  style,
  children,
}: {
  style: OffersScreenStyle;
  children: React.ReactNode;
}) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "6px 14px",
        borderRadius: 999,
        background: style.accent,
        color: style.onAccent,
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        lineHeight: 1.3,
      }}
    >
      {children}
    </span>
  );
}

// ── the rail ────────────────────────────────────────────────────────────────

type Localize = (v: LocalizedString | undefined) => string;

function OfferCard({
  offer,
  style,
  copy,
  L,
  focused,
  onMouseEnter,
  onClick,
}: {
  offer: TvRoomOffer;
  style: OffersScreenStyle;
  copy: ReturnType<typeof offersCopy>;
  L: Localize;
  focused: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const price = cardPrice(offer);
  return (
    <button
      type="button"
      data-testid={`offer-card-${offer.id}`}
      aria-current={focused ? "true" : undefined}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      style={{
        appearance: "none",
        flex: `0 0 ${CARD_W}px`,
        width: CARD_W,
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        padding: 0,
        border: `1px solid ${focused ? style.accent : style.rule}`,
        borderRadius: style.radius,
        background: style.card,
        color: style.ink,
        fontFamily: "inherit",
        cursor: "pointer",
        overflow: "hidden",
        // A ring rather than a size change: cards that grow and shrink make the
        // whole rail jump on every arrow press.
        boxShadow: focused ? `0 0 0 5px ${style.accent}55` : "none",
        transition: "box-shadow 160ms ease, border-color 160ms ease",
      }}
    >
      <div
        aria-hidden
        style={{
          flex: "0 0 auto",
          height: 240,
          background: offer.imageUrl
            ? `url(${offer.imageUrl}) center/cover`
            : style.placeholder,
        }}
      />
      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          padding: "24px 26px 26px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Badge style={style}>{copy.kind[offer.action.kind]}</Badge>
          {offer.category && (
            <span
              style={{
                fontSize: 17,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: style.inkDim,
                lineHeight: 1.3,
              }}
            >
              {offer.category}
            </span>
          )}
        </div>
        <div
          style={{
            fontFamily: style.titleFont,
            fontSize: 34,
            fontWeight: 600,
            // Clipped by the card, so never below 1.2 - see the pitfall note.
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          {L(offer.title)}
        </div>
        <div
          style={{
            fontSize: 21,
            lineHeight: 1.35,
            color: style.inkDim,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {L(offer.body)}
        </div>
        {price && (
          <div
            style={{
              marginTop: "auto",
              fontSize: 26,
              fontWeight: 700,
              lineHeight: 1.3,
              color: style.accent,
            }}
          >
            {price.from ? `${copy.from} ` : ""}
            {formatMoney(price.price, copy)}
          </div>
        )}
      </div>
    </button>
  );
}

// ── the detail page ─────────────────────────────────────────────────────────

/**
 * One offer, read at length — and everything a guest can do about it.
 *
 * **This is POS-7.** `detail → choose → confirm → pending → confirmed`, all of
 * it inside this one component so that eight hotel templates share one keyboard
 * model, and all of it through ONE picker so that a fourth kind of offer is one
 * list to build rather than a fourth flow to debug (`docs/room-offers.md` §2).
 *
 * ## The four phases and what each press means
 *
 * | Phase | OK | Back |
 * |---|---|---|
 * | `detail` | start the flow | leave to the rail |
 * | `choose` | pick / step / continue | back to `detail` |
 * | `confirm` | raise the request | back to `choose` |
 * | `sent` | withdraw, or leave | leave to the rail |
 *
 * **`choose` is skipped when there is nothing to pick**, and when there is also
 * nothing to pay the whole flow collapses to a single press: an `asap` booking
 * with no amenities behind it *is* "call for staff", and making a guest confirm
 * a free request with no options in it would be a keypress that asks nothing.
 * That is the fastest path in the product and it should feel like it.
 *
 * ## Three things that are deliberately not here
 *
 * - **No payment-method picker.** Charge-to-room is the only method in the demo
 *   (§6); the confirm step states where the money goes rather than asking.
 * - **No guest name, and no field that could take one** (§11). The room comes
 *   from the screen's own binding and from nothing a guest typed.
 * - **No local status simulation.** `pending → accepted` arrives over POS-6's
 *   realtime hub; a television that congratulated a guest on a timer would be
 *   promising something the hotel has not agreed to.
 */

type Phase = "detail" | "choose" | "confirm" | "sent";

export function OfferDetail({
  offer,
  style,
  copy,
  L,
  scopeRef,
  hints = true,
  onFlow,
  onClose,
}: {
  offer: TvRoomOffer;
  style: OffersScreenStyle;
  copy: OffersCopy;
  L: Localize;
  scopeRef: RefObject<HTMLElement>;
  hints?: boolean;
  /** True once the guest has started — the rail holds the page open then. */
  onFlow: (active: boolean) => void;
  onClose: () => void;
}) {
  const req = useGuestRequest();
  const [phase, setPhase] = useState<Phase>("detail");
  const [selection, setSelection] = useState<OfferSelection>({});
  const [party, setParty] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  // Hours the SERVER refused since this page opened. The feed still lists them
  // as free — its `remaining` was computed before the race — so the refusal is
  // remembered here and the hour greys out under the guest's hand, which is
  // what "the picker is still open and that slot is now greyed" means (§4).
  const [taken, setTaken] = useState<string[]>([]);
  // One key per confirm ACTION, minted on entering the confirm step. A double
  // press reuses it and POS-6 replays the same row; a guest who goes back,
  // changes the order and confirms again gets a new one, because that is a
  // genuinely different order.
  const idemRef = useRef("");
  // …and the guard for the press that lands while the first is still in flight.
  const busyRef = useRef(false);

  const policy = slotPolicyOf(offer.action);
  const booksHours = policy?.mode === "slots";
  const requestable = offer.action.kind !== "promotion";
  const basePrice =
    offer.action.kind === "promotion" ? undefined : offer.action.price;
  const mode: PickerMode =
    offer.action.kind === "order" ? "quantity" : booksHours ? "one" : "many";

  const rows = useMemo(
    () =>
      booksHours
        ? slotRows(offer.slots, copy, taken)
        : itemRows(offer.items, L, mode),
    [booksHours, offer.slots, offer.items, copy, L, mode, taken]
  );
  const hasChoice = rows.length > 0;
  const lines = selectedLines(rows, selection);
  // "mixed" is a refusal rather than a number — see `selectionTotal`. It is
  // pathological authoring (two currencies on one offer) and it is exactly what
  // the server answers `price_currency_mixed` to, so the television says so
  // BEFORE the guest presses Confirm rather than after.
  const totalOrMixed = selectionTotal(rows, selection, basePrice);
  const mixed = totalOrMixed === "mixed";
  const total = mixed ? null : totalOrMixed;
  const room = req.roomId ?? copy.thisRoom;
  const status: GuestRequestStatus = req.request?.status ?? "pending";

  // Which grid this phase needs. `choose` stacks the picker's rows, then the
  // party-size stepper (hours only), then the action — one `useFocusGrid` for
  // the whole page, so there is never a second listener to argue with.
  const partyRow = booksHours ? rows.length : -1;
  const actionRow = rows.length + (booksHours ? 1 : 0);
  // Withdrawing is the SECOND cell on the status page, never the first.
  //
  // Measured on a real television: a guest who presses Confirm twice — which
  // this flow is built to survive — landed on the status page with the second
  // press still travelling, and cancelled the order they had just placed. A
  // double press producing one request and then withdrawing it is worse than a
  // duplicate. So OK on the status page means "Klar" and withdrawing costs a
  // deliberate press of ►.
  const canWithdraw = !!req.request && guestMayCancel(status);
  const dims =
    phase === "choose"
      ? { rows: actionRow + 1, cols: pickerColumns(rows) }
      : { rows: 1, cols: phase === "sent" && canWithdraw ? 2 : 1 };

  const [focus, setFocus] = useFocusGrid({
    rows: dims.rows,
    cols: dims.cols,
    scopeRef,
    wrap: true,
    onEnter: (f) => press(f),
    onBack: () => back(),
  });
  // A phase change resizes the grid under the focus it was holding, so the ring
  // would otherwise land on a row that no longer exists.
  //
  // It does NOT clear the message, and that is the whole point of the
  // overbooking path: "Den tiden är tagen — välj en annan" is set by a refusal
  // that ALSO sends the guest back to the picker, so a blanket clear here wiped
  // the only explanation they were ever going to get. Messages are cleared
  // where they stop being true instead — a fresh pick, a fresh start, a Back.
  // (Seen on a real television, 2026-08-14: the hour greyed out and nothing
  // said why.)
  useEffect(() => {
    setFocus({ r: 0, c: 0 });
    // `setFocus` is stable; re-running this on it would fight the guest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => onFlow(phase !== "detail"), [phase, onFlow]);

  // An open — the funnel's third stage (POS-11). Once per mount, and the page
  // is remounted per offer, so backing out and opening the same card again is a
  // second open, which it genuinely is.
  //
  // The offer id and its title, and nothing else: no room, no price, no request
  // id. See the rail's `offer_view` for why an offer id is not one of those.
  const countedRef = useRef(false);
  useEffect(() => {
    if (countedRef.current) return;
    countedRef.current = true;
    trackEngagement("offer_open", offer.id, L(offer.title) || undefined);
  }, [offer.id, offer.title, L]);

  /** The focused row's item, with `c` clamped to a row that may be shorter. */
  const itemAt = useCallback(
    (f: { r: number; c: number }): PickerItem | null => {
      const row = rows[f.r];
      if (!row || row.items.length === 0) return null;
      return row.items[Math.min(f.c, row.items.length - 1)] ?? null;
    },
    [rows]
  );

  const payload = useCallback(() => {
    const slotStart = booksHours ? Object.keys(selection)[0] : undefined;
    return {
      selection: booksHours
        ? []
        : lines.map((l) => ({ itemId: l.item.id, quantity: l.quantity })),
      ...(slotStart ? { slotStart } : {}),
      ...(booksHours ? { partySize: party } : {}),
    };
  }, [booksHours, selection, lines, party]);

  /** Raise the request. The one place a press turns into money. */
  const submit = useCallback(async () => {
    // A double press must produce ONE request: the second press finds the guard
    // up, and the key underneath it means even a genuine retry is the same row.
    if (busyRef.current) return;
    busyRef.current = true;
    if (!idemRef.current) idemRef.current = `gr-${nanoid(16)}`;
    const body = payload();
    const outcome = await req.send(offer.id, body, idemRef.current);
    busyRef.current = false;
    if (outcome.ok) {
      setMessage(null);
      setPhase("sent");
      return;
    }
    setMessage(copy.errors[outcome.code]);
    // The overbooking loser. The hour is greyed, the pick is dropped and the
    // picker comes straight back — never a dead end, never a generic error.
    if (
      (outcome.code === "slot_taken" || outcome.code === "slot_unavailable") &&
      body.slotStart
    ) {
      const lost = body.slotStart;
      setTaken((t) => (t.includes(lost) ? t : [...t, lost]));
      setSelection({});
      idemRef.current = "";
      if (hasChoice) setPhase("choose");
    }
  }, [payload, req, offer.id, copy, hasChoice]);

  const cancel = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    const outcome = await req.cancel();
    busyRef.current = false;
    if (!outcome.ok) setMessage(copy.errors[outcome.code]);
  }, [req, copy]);

  /** OK on the detail page: as far into the flow as this offer actually needs. */
  const start = useCallback(() => {
    if (!requestable) return; // a promotion has nothing behind it (§2)
    setMessage(null);
    if (booksHours && rows.length === 0) {
      setMessage(copy.soldOut);
      return;
    }
    if (hasChoice) {
      setPhase("choose");
      return;
    }
    if (basePrice) {
      idemRef.current = "";
      setPhase("confirm");
      return;
    }
    // Nothing to pick and nothing to pay: one press calls staff.
    void submit();
  }, [
    requestable,
    booksHours,
    rows.length,
    hasChoice,
    basePrice,
    copy,
    submit,
  ]);

  /** Pick (or refuse) one tile. The keyboard and the mouse land here alike. */
  const pickItem = useCallback(
    (item: PickerItem) => {
      if (item.disabled) {
        // Greyed, focusable, and refused with words rather than with nothing.
        setMessage(copy.errors.slot_taken);
        return;
      }
      setMessage(null);
      setSelection((s) => applyPick(s, item, mode));
    },
    [copy, mode]
  );

  /** OK on the choose step's action: everything picked, on to the last word. */
  const toConfirm = useCallback(() => {
    if (lines.length === 0) {
      setMessage(copy.pickSomething);
      return;
    }
    if (mixed) {
      setMessage(copy.errors.price_currency_mixed);
      return;
    }
    setMessage(null);
    idemRef.current = "";
    setPhase("confirm");
  }, [lines.length, mixed, copy]);

  function press(f: { r: number; c: number }) {
    if (phase === "detail") return start();
    if (phase === "choose") {
      if (f.r === partyRow) {
        setParty((p) => (p >= MAX_QUANTITY ? 1 : p + 1));
        return;
      }
      if (f.r === actionRow) return toConfirm();
      const item = itemAt(f);
      if (item) pickItem(item);
      return;
    }
    if (phase === "confirm") return void submit();
    // `sent`: the first cell leaves, the second withdraws. See `canWithdraw`.
    if (canWithdraw && f.c === 1) return void cancel();
    onClose();
  }

  function back() {
    setMessage(null);
    if (phase === "choose") return setPhase("detail");
    if (phase === "confirm") return setPhase(hasChoice ? "choose" : "detail");
    onClose();
  }

  // ◄ ► on a priced line and on the party-size stepper. They cannot come from
  // the focus grid: there they MOVE, and a stepper needs them to COUNT. The
  // grid still sees them (its own column move is a no-op on a one-wide row),
  // so this is an addition rather than an interception.
  useStepKeys({
    enabled: phase === "choose",
    scopeRef,
    onStep: (delta) => {
      if (focus.r === partyRow) {
        setParty((p) => Math.max(1, Math.min(MAX_QUANTITY, p + delta)));
        return;
      }
      if (mode !== "quantity") return;
      const item = itemAt(focus);
      if (!item) return;
      setMessage(null);
      setSelection((s) => applyStep(s, item, delta));
    },
  });

  const frame: CSSProperties = {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 18,
    overflow: "hidden",
  };

  /** What the guest picked, as one line of text. Used by confirm and by sent. */
  const summary = summaryLine(lines, booksHours, offer.slots, party, copy);

  return (
    <div data-testid={`offer-detail-${offer.id}`} style={frame}>
      {phase === "detail" && (
        <DetailBody
          offer={offer}
          style={style}
          copy={copy}
          L={L}
          action={
            requestable
              ? {
                  label: startLabel(offer, hasChoice, copy),
                  focused: true,
                  disabled: booksHours && rows.length === 0,
                  onPress: start,
                }
              : null
          }
          message={message}
        />
      )}

      {phase === "choose" && (
        <ChooseBody
          offer={offer}
          rows={rows}
          selection={selection}
          mode={mode}
          style={style}
          copy={copy}
          L={L}
          focus={focus}
          setFocus={setFocus}
          onPick={pickItem}
          onContinue={toConfirm}
          party={booksHours ? party : null}
          partyRow={partyRow}
          actionRow={actionRow}
          chosen={lines.length}
          total={total}
          mixed={mixed}
          message={message}
        />
      )}

      {phase === "confirm" && (
        <ConfirmBody
          title={L(offer.title)}
          summary={summary}
          sentence={
            total
              ? copy.chargeRoom(formatMoney(total, copy), room)
              : copy.chargeNone(room)
          }
          total={total}
          style={style}
          copy={copy}
          sending={req.sending}
          message={message}
          onPress={() => void submit()}
        />
      )}

      {phase === "sent" && (
        <SentBody
          title={L(offer.title)}
          summary={summary}
          status={status}
          total={total}
          style={style}
          copy={copy}
          message={message}
          reason={refusalReason(req.request)}
          canWithdraw={canWithdraw}
          focusedCell={Math.min(focus.c, canWithdraw ? 1 : 0)}
          onDone={onClose}
          onWithdraw={() => void cancel()}
        />
      )}

      {hints && (
        <Hints
          style={style}
          items={hintsFor(phase, mode, requestable, copy, status)}
        />
      )}
    </div>
  );
}

/**
 * ◄ ► as a counter rather than as a move.
 *
 * Scoped exactly like `useFocusGrid` — only while focus is inside the screen —
 * so two schemes or two screens can never both step something. Bound through a
 * ref for the same reason the grid does it: one listener, always the current
 * handler, never a closure that believes the guest is still on the first line.
 */
function useStepKeys({
  enabled,
  scopeRef,
  onStep,
}: {
  enabled: boolean;
  scopeRef: RefObject<HTMLElement>;
  onStep: (delta: number) => void;
}) {
  const ref = useRef(onStep);
  useEffect(() => {
    ref.current = onStep;
  });
  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      const el = scopeRef.current;
      if (!el) return;
      if (!el.contains(document.activeElement) && document.activeElement !== el)
        return;
      if (e.key === "ArrowLeft") ref.current(-1);
      else if (e.key === "ArrowRight") ref.current(1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [enabled, scopeRef]);
}

/**
 * Why the hotel refused, in their own words — when they gave any.
 *
 * **POS-9** stamps an optional `reason` on a transition and means it to reach
 * the guest's television: "Köket har stängt" is an answer, and a bare "Vi kunde
 * inte ta emot det" is not. It is read off the LAST stamp, defensively rather
 * than through the type, because the field belongs to POS-9's ticket and this
 * bundle may be older than it — a cast here is one line, whereas widening
 * `GuestRequestStamp` from this side would be two tickets editing one type.
 *
 * The blank case is not an error: most refusals carry no reason at all, and the
 * status copy already names the next action.
 */
function refusalReason(request: GuestRequest | null): string | null {
  const stamps = request?.stamps ?? [];
  const last = stamps[stamps.length - 1] as { reason?: unknown } | undefined;
  const reason = typeof last?.reason === "string" ? last.reason.trim() : "";
  return reason || null;
}

/** The word on the detail page's one button. */
function startLabel(
  offer: TvRoomOffer,
  hasChoice: boolean,
  copy: OffersCopy
): string {
  if (offer.action.kind === "order") return copy.start.order;
  const policy = slotPolicyOf(offer.action);
  if (policy?.mode === "slots") return copy.start.book;
  return hasChoice ? copy.start.ask : copy.start.call;
}

/**
 * Bookable hours as picker rows: one row per DAY, headed with that day.
 *
 * This is the boundary the doctrine draws — a generated slot becomes an
 * `OfferItem`-shaped thing (`{ id: "2026-08-20T19:00", label: "19:00" }`) and
 * the picker never learns what a time is. The day lives in the row heading, so
 * the tile can be the hour alone and a guest reads a week the way they read a
 * timetable.
 *
 * A full hour is kept, greyed and focusable. "18:00 is gone" is information a
 * guest wants, and an hour that silently vanished reads as a bug.
 */
function slotRows(
  slots: OfferSlotAvailability[],
  copy: OffersCopy,
  taken: string[]
): PickerRow[] {
  const byDay: Array<{ heading: string; items: PickerItem[] }> = [];
  for (const slot of slots) {
    const heading = formatSlotDay(slot, copy);
    const full = slot.remaining <= 0 || taken.includes(slot.start);
    const item: PickerItem = {
      id: slot.start,
      label: slot.time,
      note: full ? copy.soldOut : copy.seatsLeft(slot.remaining),
      disabled: full,
    };
    const last = byDay[byDay.length - 1];
    // Chunked so a 30-minute template cannot run a day off the side of the
    // panel; the heading is printed once and the overflow reads as a second
    // line of the same day.
    if (last && last.heading === heading && last.items.length < SLOTS_PER_ROW) {
      last.items.push(item);
    } else {
      byDay.push({ heading, items: [item] });
    }
  }
  return byDay.map((row, i) => ({
    heading: i > 0 && byDay[i - 1].heading === row.heading ? "" : row.heading,
    items: row.items,
  }));
}

/** Authored lines as picker rows: one per row when priced, four across when not. */
function itemRows(
  items: OfferItem[],
  L: Localize,
  mode: PickerMode
): PickerRow[] {
  const per = mode === "quantity" ? 1 : ITEMS_PER_ROW;
  const rows: PickerRow[] = [];
  for (const item of items) {
    const picker: PickerItem = {
      id: item.id,
      label: L(item.label),
      // Always an icon, even when the operator picked none: `offerIconKey`
      // answers `generic` for an absent key, and a tile with no glyph sits at a
      // different height from its neighbours, which reads as a rendering fault.
      icon: item.icon ?? "",
      imageUrl: item.imageUrl,
      ...(item.price ? { price: item.price } : {}),
    };
    const last = rows[rows.length - 1];
    if (last && last.items.length < per) last.items.push(picker);
    else rows.push({ items: [picker] });
  }
  return rows;
}

/** One line of text for what is about to happen, or what already did. */
function summaryLine(
  lines: Array<{ item: PickerItem; quantity: number }>,
  booksHours: boolean,
  slots: OfferSlotAvailability[],
  party: number,
  copy: OffersCopy
): string {
  if (booksHours) {
    const slot = slots.find((s) => s.start === lines[0]?.item.id);
    // Printed from the slot's own parts — never by parsing the wall clock into
    // a Date, which would re-interpret it in the panel's own zone.
    const when = slot ? formatSlot(slot, copy) : "";
    return [when, copy.partySize(party)].filter(Boolean).join(" · ");
  }
  return lines.map((l) => `${l.quantity} × ${l.item.label}`).join(" · ");
}

/** The remote hints for a phase. */
function hintsFor(
  phase: Phase,
  mode: PickerMode,
  requestable: boolean,
  copy: OffersCopy,
  status: GuestRequestStatus
): Array<{ key: string; label: string }> {
  if (phase === "choose") {
    return [
      { key: "▲ ▼", label: copy.hint.move },
      ...(mode === "quantity"
        ? [{ key: "◄ ►", label: copy.hint.step }]
        : [{ key: "◄ ►", label: copy.hint.move }]),
      { key: "OK", label: copy.hint.pick },
      { key: "←", label: copy.hint.back },
    ];
  }
  if (phase === "confirm") {
    return [
      { key: "OK", label: copy.hint.send },
      { key: "←", label: copy.hint.back },
    ];
  }
  if (phase === "sent") {
    return [
      ...(guestMayCancel(status)
        ? [{ key: "◄ ►", label: copy.cancelLabel }]
        : []),
      { key: "OK", label: copy.doneLabel },
      { key: "←", label: copy.hint.back },
    ];
  }
  return [
    ...(requestable ? [{ key: "OK", label: copy.hint.open }] : []),
    { key: "←", label: copy.hint.back },
  ];
}

function DetailBody({
  offer,
  style,
  copy,
  L,
  action,
  message,
}: {
  offer: TvRoomOffer;
  style: OffersScreenStyle;
  copy: OffersCopy;
  L: Localize;
  action: {
    label: string;
    focused: boolean;
    disabled: boolean;
    onPress: () => void;
  } | null;
  message: string | null;
}) {
  const price =
    offer.action.kind === "promotion" ? undefined : offer.action.price;
  const meta: string[] = [];
  if (offer.action.kind === "order")
    meta.push(copy.fulfil[offer.action.fulfil]);
  if (offer.action.kind === "booking" && offer.action.slots.mode === "asap") {
    meta.push(copy.asap);
  }
  if (price) meta.push(formatMoney(price, copy));

  return (
    <div
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        gap: 44,
        overflow: "hidden",
      }}
    >
      {offer.imageUrl && (
        <div
          aria-hidden
          style={{
            flex: "0 0 640px",
            borderRadius: style.radius,
            background: `url(${offer.imageUrl}) center/cover`,
          }}
        />
      )}
      <div
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 20,
          overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Badge style={style}>{copy.kind[offer.action.kind]}</Badge>
          {offer.category && (
            <span
              style={{
                fontSize: 15,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: style.inkDim,
                lineHeight: 1.3,
              }}
            >
              {offer.category}
            </span>
          )}
        </div>
        <h2
          style={{
            margin: 0,
            fontFamily: style.titleFont,
            fontSize: 52,
            fontWeight: 600,
            lineHeight: 1.2,
            letterSpacing: "-0.01em",
          }}
        >
          {L(offer.title)}
        </h2>
        <div style={{ fontSize: 26, lineHeight: 1.45, color: style.inkDim }}>
          {L(offer.body)}
        </div>
        {meta.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 18,
              fontSize: 24,
              fontWeight: 600,
              lineHeight: 1.3,
              color: style.accent,
            }}
          >
            {meta.map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>
        )}

        {offer.slots.length > 0 && (
          <Section title={copy.slotsTitle} style={style}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {offer.slots.slice(0, SLOT_PREVIEW).map((slot) => (
                <SlotChip
                  key={slot.start}
                  slot={slot}
                  style={style}
                  copy={copy}
                />
              ))}
              {offer.slots.length > SLOT_PREVIEW && (
                <span
                  style={{
                    alignSelf: "center",
                    fontSize: 20,
                    lineHeight: 1.3,
                    color: style.inkDim,
                  }}
                >
                  {copy.moreTimes(offer.slots.length - SLOT_PREVIEW)}
                </span>
              )}
            </div>
          </Section>
        )}

        {offer.items.length > 0 && (
          <Section title={copy.itemsTitle} style={style}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {offer.items.slice(0, ITEM_PREVIEW).map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  style={style}
                  copy={copy}
                  L={L}
                />
              ))}
            </div>
            {offer.items.length > ITEM_PREVIEW && (
              <div
                style={{
                  marginTop: 10,
                  fontSize: 20,
                  lineHeight: 1.3,
                  color: style.inkDim,
                }}
              >
                {copy.moreItems(offer.items.length - ITEM_PREVIEW)}
              </div>
            )}
          </Section>
        )}

        {L(offer.terms).trim() && (
          <Section title={copy.termsTitle} style={style}>
            <div
              style={{ fontSize: 19, lineHeight: 1.45, color: style.inkDim }}
            >
              {L(offer.terms)}
            </div>
          </Section>
        )}

        {message && <MessageLine style={style}>{message}</MessageLine>}

        {action && (
          <div style={{ marginTop: "auto", paddingTop: 8 }}>
            <ActionButton
              testid="offer-start"
              style={style}
              focused={action.focused}
              disabled={action.disabled}
              onPress={action.onPress}
            >
              {action.disabled ? copy.soldOut : action.label}
            </ActionButton>
          </div>
        )}
      </div>
    </div>
  );
}

function ChooseBody({
  offer,
  rows,
  selection,
  mode,
  style,
  copy,
  L,
  focus,
  setFocus,
  onPick,
  onContinue,
  party,
  partyRow,
  actionRow,
  chosen,
  total,
  mixed,
  message,
}: {
  offer: TvRoomOffer;
  rows: PickerRow[];
  selection: OfferSelection;
  mode: PickerMode;
  style: OffersScreenStyle;
  copy: OffersCopy;
  L: Localize;
  focus: { r: number; c: number };
  setFocus: (next: { r: number; c: number }) => void;
  onPick: (item: PickerItem) => void;
  onContinue: () => void;
  party: number | null;
  partyRow: number;
  actionRow: number;
  chosen: number;
  total: Money | null;
  /** Two currencies on one offer — a price the television will not invent. */
  mixed: boolean;
  message: string | null;
}) {
  return (
    <>
      <div style={{ flex: "0 0 auto" }}>
        <div
          style={{
            fontSize: 17,
            letterSpacing: "0.28em",
            textTransform: "uppercase",
            color: style.accent,
            fontWeight: 600,
            lineHeight: 1.3,
            marginBottom: 8,
          }}
        >
          {L(offer.title)}
        </div>
        <h2
          style={{
            margin: 0,
            fontFamily: style.titleFont,
            fontSize: 40,
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          {mode === "one" ? copy.slotsTitle : copy.chooseTitle}
        </h2>
      </div>

      <OfferPicker
        rows={rows}
        selection={selection}
        mode={mode}
        style={style}
        copy={copy}
        focus={focus}
        onHover={(r, c) => setFocus({ r, c })}
        onPick={onPick}
      />

      {party !== null && (
        <StepperRow
          testid="offer-party"
          style={style}
          label={copy.partyTitle}
          value={copy.partySize(party)}
          focused={focus.r === partyRow}
          onHover={() => setFocus({ r: partyRow, c: 0 })}
        />
      )}

      {message && <MessageLine style={style}>{message}</MessageLine>}

      <div
        style={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 24,
        }}
      >
        <div
          data-testid="offer-total"
          style={{ fontSize: 26, lineHeight: 1.3, color: style.inkDim }}
        >
          {mixed ? (
            <span style={{ color: style.accent, fontWeight: 700 }}>
              {copy.errors.price_currency_mixed}
            </span>
          ) : total ? (
            <>
              {copy.totalTitle}{" "}
              <span style={{ color: style.accent, fontWeight: 700 }}>
                {formatMoney(total, copy)}
              </span>
            </>
          ) : (
            <>
              {copy.chosenCount(chosen)}
              {" · "}
              <span style={{ color: style.accent, fontWeight: 700 }}>
                {copy.free}
              </span>
            </>
          )}
        </div>
        <div style={{ marginLeft: "auto" }}>
          <ActionButton
            testid="offer-continue"
            style={style}
            focused={focus.r === actionRow}
            onPress={onContinue}
          >
            {copy.continueLabel}
          </ActionButton>
        </div>
      </div>
    </>
  );
}

function ConfirmBody({
  title,
  summary,
  sentence,
  total,
  style,
  copy,
  sending,
  message,
  onPress,
}: {
  title: string;
  summary: string;
  sentence: string;
  total: Money | null;
  style: OffersScreenStyle;
  copy: OffersCopy;
  sending: boolean;
  message: string | null;
  onPress: () => void;
}) {
  return (
    <div
      data-testid="offer-confirm"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 18,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 17,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: style.accent,
          fontWeight: 600,
          lineHeight: 1.3,
        }}
      >
        {copy.confirmTitle}
      </div>
      <h2
        style={{
          margin: 0,
          fontFamily: style.titleFont,
          fontSize: 46,
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {title}
      </h2>
      {summary && (
        <div style={{ fontSize: 30, lineHeight: 1.35, color: style.ink }}>
          {summary}
        </div>
      )}
      {/* The last honest sentence: what it costs and which room it lands on.
          A zero-priced thing says "no charge" rather than "0 kr" — an amenity
          is free, not worth nothing. */}
      <div style={{ fontSize: 28, lineHeight: 1.35, color: style.inkDim }}>
        {sentence}
      </div>
      {total && (
        <div
          data-testid="offer-confirm-total"
          style={{
            fontSize: 44,
            fontWeight: 700,
            lineHeight: 1.25,
            color: style.accent,
          }}
        >
          {formatMoney(total, copy)}
        </div>
      )}
      {message && <MessageLine style={style}>{message}</MessageLine>}
      <div style={{ marginTop: "auto" }}>
        <ActionButton
          testid="offer-confirm-button"
          style={style}
          focused
          onPress={onPress}
        >
          {sending ? copy.sending : copy.confirmButton}
        </ActionButton>
      </div>
    </div>
  );
}

function SentBody({
  title,
  summary,
  status,
  total,
  style,
  copy,
  message,
  reason,
  canWithdraw,
  focusedCell,
  onDone,
  onWithdraw,
}: {
  title: string;
  summary: string;
  status: GuestRequestStatus;
  total: Money | null;
  style: OffersScreenStyle;
  copy: OffersCopy;
  message: string | null;
  /** What staff typed when they refused it (POS-9). Usually absent. */
  reason: string | null;
  /** Nobody has touched the request yet, so the guest may still take it back. */
  canWithdraw: boolean;
  focusedCell: number;
  onDone: () => void;
  onWithdraw: () => void;
}) {
  return (
    <div
      data-testid={`offer-status-${status}`}
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 18,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          fontSize: 17,
          letterSpacing: "0.28em",
          textTransform: "uppercase",
          color: style.accent,
          fontWeight: 600,
          lineHeight: 1.3,
        }}
      >
        {title}
      </div>
      <h2
        style={{
          margin: 0,
          fontFamily: style.titleFont,
          fontSize: 52,
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {copy.statusTitle[status]}
      </h2>
      <div style={{ fontSize: 28, lineHeight: 1.35, color: style.inkDim }}>
        {copy.statusBody[status]}
      </div>
      {reason && (
        <div
          data-testid="offer-status-reason"
          style={{ fontSize: 26, lineHeight: 1.35, color: style.ink }}
        >
          {/* The hotel's own words, quoted rather than paraphrased: an answer a
              guest can act on beats a status they cannot. */}
          {`”${reason}”`}
        </div>
      )}
      {summary && (
        <div style={{ fontSize: 26, lineHeight: 1.35, color: style.ink }}>
          {summary}
          {total ? ` · ${formatMoney(total, copy)}` : ""}
        </div>
      )}
      {message && <MessageLine style={style}>{message}</MessageLine>}
      <div style={{ marginTop: "auto", display: "flex", gap: 18 }}>
        <ActionButton
          testid="offer-status-button"
          style={style}
          focused={focusedCell === 0}
          onPress={onDone}
        >
          {copy.doneLabel}
        </ActionButton>
        {canWithdraw && (
          <ActionButton
            testid="offer-cancel-button"
            style={style}
            focused={focusedCell === 1}
            onPress={onWithdraw}
          >
            {copy.cancelLabel}
          </ActionButton>
        )}
      </div>
    </div>
  );
}

/** The party-size stepper: one focus cell, counted with ◄ ►. */
function StepperRow({
  testid,
  style,
  label,
  value,
  focused,
  onHover,
}: {
  testid: string;
  style: OffersScreenStyle;
  label: string;
  value: string;
  focused: boolean;
  onHover: () => void;
}) {
  return (
    <div
      data-testid={testid}
      aria-current={focused ? "true" : undefined}
      onMouseEnter={onHover}
      style={{
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        gap: 18,
        padding: "12px 20px",
        borderRadius: style.radius,
        border: `1px solid ${focused ? style.accent : style.rule}`,
        background: style.card,
        boxShadow: focused ? `0 0 0 5px ${style.accent}55` : "none",
        fontSize: 24,
        lineHeight: 1.3,
      }}
    >
      <span style={{ color: style.inkDim }}>{label}</span>
      <span style={{ marginLeft: "auto", opacity: focused ? 1 : 0 }}>‹</span>
      <span style={{ fontWeight: 700 }}>{value}</span>
      <span style={{ opacity: focused ? 1 : 0 }}>›</span>
    </div>
  );
}

/**
 * The one button shape the flow uses.
 *
 * A `<button>` rather than a `<div>` so the CMS preview and a mouse can press
 * it; the remote never touches it — the focus grid owns OK, and this is the
 * ring that says where OK will land.
 */
function ActionButton({
  testid,
  style,
  focused,
  disabled,
  onPress,
  children,
}: {
  testid: string;
  style: OffersScreenStyle;
  focused: boolean;
  disabled?: boolean;
  onPress?: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      aria-current={focused ? "true" : undefined}
      aria-disabled={disabled ? "true" : undefined}
      onClick={() => !disabled && onPress?.()}
      style={{
        appearance: "none",
        fontFamily: "inherit",
        cursor: "pointer",
        padding: "18px 42px",
        borderRadius: style.radius,
        border: `1px solid ${focused && !disabled ? style.accent : style.rule}`,
        background: focused && !disabled ? style.accent : style.card,
        color: focused && !disabled ? style.onAccent : style.ink,
        opacity: disabled ? 0.5 : 1,
        fontSize: 28,
        fontWeight: 700,
        lineHeight: 1.3,
        boxShadow:
          focused && !disabled ? `0 0 0 5px ${style.accent}55` : "none",
        transition: "box-shadow 160ms ease, background 160ms ease",
      }}
    >
      {children}
    </button>
  );
}

/**
 * A refusal, in words, naming what to do next.
 *
 * Never "Något gick fel": a dead end on a television is worse than an error,
 * because a guest cannot open a console, cannot retry with a keyboard and
 * cannot see what went wrong.
 */
function MessageLine({
  style,
  children,
}: {
  style: OffersScreenStyle;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid="offer-message"
      role="status"
      style={{
        flex: "0 0 auto",
        fontSize: 26,
        lineHeight: 1.35,
        fontWeight: 600,
        color: style.accent,
      }}
    >
      {children}
    </div>
  );
}

function Section({
  title,
  style,
  children,
}: {
  title: string;
  style: OffersScreenStyle;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 17,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: style.inkDim,
          fontWeight: 700,
          lineHeight: 1.3,
          marginBottom: 12,
          paddingTop: 14,
          borderTop: `1px solid ${style.rule}`,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

/**
 * One bookable hour with its remaining seats.
 *
 * `remaining` is Foyer's own ledger — how many of this hour the hotel chose to
 * sell through the television, minus what has been taken — and never a claim
 * about how many seats exist in the spa (`docs/room-offers.md` §4). A full hour
 * is shown greyed rather than hidden, because "18:00 is gone" is information a
 * guest wants and a silently missing hour reads as a bug.
 */
function SlotChip({
  slot,
  style,
  copy,
}: {
  slot: OfferSlotAvailability;
  style: OffersScreenStyle;
  copy: ReturnType<typeof offersCopy>;
}) {
  const full = slot.remaining <= 0;
  return (
    <div
      data-testid={`offer-slot-${slot.start}`}
      style={{
        padding: "12px 18px",
        borderRadius: style.radius,
        border: `1px solid ${style.rule}`,
        background: style.card,
        opacity: full ? 0.5 : 1,
        lineHeight: 1.3,
      }}
    >
      <div style={{ fontSize: 24, fontWeight: 600 }}>
        {formatSlot(slot, copy)}
      </div>
      <div style={{ fontSize: 17, color: style.inkDim, marginTop: 2 }}>
        {full ? copy.soldOut : copy.seatsLeft(slot.remaining)}
      </div>
    </div>
  );
}

/**
 * One authored line.
 *
 * `OfferItem.icon` is a catalogue key with no catalogue behind it yet, so the
 * line renders its `imageUrl` when the operator supplied one and its own words
 * otherwise. Inventing a glyph set here would be a second icon vocabulary for
 * POS-5's editor to disagree with.
 */
function ItemRow({
  item,
  style,
  copy,
  L,
}: {
  item: OfferItem;
  style: OffersScreenStyle;
  copy: ReturnType<typeof offersCopy>;
  L: Localize;
}) {
  return (
    <div
      data-testid={`offer-item-${item.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 16px",
        borderRadius: style.radius,
        border: `1px solid ${style.rule}`,
        background: style.card,
      }}
    >
      {item.imageUrl && (
        <span
          aria-hidden
          style={{
            flex: "0 0 auto",
            width: 44,
            height: 44,
            borderRadius: 8,
            background: `url(${item.imageUrl}) center/cover`,
          }}
        />
      )}
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          fontSize: 22,
          lineHeight: 1.3,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {L(item.label)}
      </span>
      <span
        style={{
          flex: "0 0 auto",
          fontSize: 21,
          fontWeight: 600,
          lineHeight: 1.3,
          color: item.price ? style.accent : style.inkDim,
        }}
      >
        {item.price ? formatMoney(item.price, copy) : copy.free}
      </span>
    </div>
  );
}
